import React, { ReactNode, useMemo } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  useController,
  UseFormTrigger,
} from "react-hook-form";

import {
  AvatarItem,
  AvatarStack,
} from "../../../../../components/newUI/avatar";
import { FieldLabel } from "../../../../../components/newUI/text";
import CustomSelect from "../../../../../components/select/Select";
import { SelectOptionType } from "../../../../../types/types";
import { CREATE_ROLE_OPTION, useCreateRole } from "../hooks/useCreateRole";
import { MAX_ASSIGNEES } from "../model/addWork.constants";
import { AddWorkFormData } from "../model/addWork.types";

type AssignmentSectionProps = {
  control: Control<AddWorkFormData>;
  errors: FieldErrors<AddWorkFormData>;
  usersOptions: SelectOptionType[];
  roleOptions: SelectOptionType[];
  selectedAssigneeItems: AvatarItem[];
  selectedRoleItems: AvatarItem[];
  isLoadingUsers: boolean;
  isLoadingRoles: boolean;
  isLoadingWorkTypeDefaults: boolean;
  hasRoleOrAssignee: () => boolean;
  trigger: UseFormTrigger<AddWorkFormData>;
  renderRoleMembersPreview: (option: SelectOptionType) => ReactNode;
};

export const AssignmentSection: React.FC<AssignmentSectionProps> = ({
  control,
  errors,
  usersOptions,
  roleOptions,
  selectedAssigneeItems,
  selectedRoleItems,
  isLoadingUsers,
  isLoadingRoles,
  isLoadingWorkTypeDefaults,
  hasRoleOrAssignee,
  trigger,
  renderRoleMembersPreview,
}) => {
  const {
    field: { value: role, onChange: onRoleChange },
  } = useController({
    name: "role",
    control,
    rules: {
      validate: () =>
        hasRoleOrAssignee() ? true : "Select a role or at least one assignee",
    },
  });
  const { startCreateRole, createRoleModals } = useCreateRole((newRole) => {
    onRoleChange(newRole);
    void trigger(["assignedTo", "role"]);
  });
  const roleOptionsWithCreate = useMemo(
    () => [CREATE_ROLE_OPTION, ...roleOptions],
    [roleOptions],
  );

  return (
    <div className={"rounded-lg border border-gray-200 bg-gray-100 p-3"}>
      <p className={"mb-2 text-sm font-bold uppercase text-[#9097A8]"}>
        Assignment<span className={"text-[#DC2626]"}>*</span>
      </p>
      <div className={"mb-4"}>
        <FieldLabel
          label={"Assignee(s)"}
          infoTooltip={`Select up to ${MAX_ASSIGNEES} assignees. A role or at least one assignee is required.`}
          tooltipId={"add-work-assignee-tooltip"}
        />
        <div className={"grid grid-cols-[1fr_128px] items-center gap-2"}>
          <Controller
            name={"assignedTo"}
            control={control}
            rules={{
              validate: () =>
                hasRoleOrAssignee()
                  ? true
                  : "Select a role or at least one assignee",
            }}
            render={({ field: { value, onChange } }) => (
              <CustomSelect
                placeholder={"Select assignee(s)"}
                options={usersOptions}
                value={value}
                onChange={(newValue) => {
                  const nextAssignees = newValue as SelectOptionType[];

                  if (nextAssignees.length <= MAX_ASSIGNEES) {
                    onChange(nextAssignees);
                    void trigger(["assignedTo", "role"]);
                  }
                }}
                withMenu
                isMulti
                isLoading={isLoadingUsers || isLoadingWorkTypeDefaults}
                isError={!!errors?.assignedTo}
                selectStyle={{
                  fullWidth: true,
                  size: "md",
                  listHeight: "380px",
                  fitChips: true,
                  controlStyle: { backgroundColor: "#fff" },
                }}
              />
            )}
          />
          <AvatarStack
            items={selectedAssigneeItems}
            maxItems={5}
            avatarPlaceholderClassNames={"bg-gray-100"}
          />
        </div>
        {errors?.assignedTo?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors?.assignedTo?.message}
          </div>
        )}
      </div>

      <div>
        <FieldLabel
          label={"Role"}
          infoTooltip={
            "Select one role. A role or at least one assignee is required."
          }
        />
        <div className={"grid grid-cols-[1fr_128px] items-center gap-2"}>
          <CustomSelect
            placeholder={"Select role"}
            options={roleOptionsWithCreate}
            value={role}
            onChange={(newValue) => {
              const newSelectedRole = newValue as SelectOptionType;
              if (newSelectedRole.value === CREATE_ROLE_OPTION.value) {
                startCreateRole();
                return;
              }
              onRoleChange(newSelectedRole.value ? newSelectedRole : null);
              void trigger(["assignedTo", "role"]);
            }}
            withMenu
            isLoading={isLoadingRoles || isLoadingWorkTypeDefaults}
            renderOptionPreview={(option) =>
              option.value === CREATE_ROLE_OPTION.value
                ? null
                : renderRoleMembersPreview(option)
            }
            isError={!!errors?.role}
            selectStyle={{
              fullWidth: true,
              size: "md",
              controlStyle: { backgroundColor: "#fff" },
            }}
          />
          <AvatarStack
            items={selectedRoleItems}
            avatarPlaceholderClassNames={"bg-gray-50"}
            avatarCountClassNames={"bg-gray-50"}
          />
        </div>
        {errors?.role?.message && (
          <div className={"mt-2 text-sm font-bold text-error"}>
            {errors?.role?.message}
          </div>
        )}
      </div>
      {createRoleModals}
    </div>
  );
};
